import { useCallback, useEffect, useMemo, useState } from 'react';
import { canAccessMainApp } from '../../utils/authHelpers';
import { useSession } from '../SessionContext';
import {
    EMPTY_PAPER_INBOX_PAGE_DATA,
    fetchPaperInboxPageDataAfterReadiness,
    type PaperInboxPageData,
} from '../paperInboxData';
import {
    buildForwardedOnboardingSnapshot,
    fallbackForwardedImportMessage,
    type ForwardedOnboardingSnapshot,
} from '../forwardedOnboarding';
import { useUserProjects } from '../useUserProjects';
import { msg } from '@lingui/core/macro';
import type { LocalizedMessage } from '../i18n/messages';

interface UseForwardedOnboardingStateOptions {
    skip?: boolean;
    pageSize?: number;
    nonInboxProjectCount?: number;
    areProjectsLoading?: boolean;
    projectsError?: LocalizedMessage | null;
}

interface UseForwardedOnboardingStateResult extends ForwardedOnboardingSnapshot {
    nonInboxProjectCount: number;
    fallbackMessage: LocalizedMessage;
    isEligible: boolean;
    isLoading: boolean;
    error: LocalizedMessage | null;
    refresh: () => Promise<void>;
}

function emptySnapshot(): ForwardedOnboardingSnapshot {
    return {
        importCount: 0,
        importedPaperCount: 0,
        previewItems: [],
    };
}

function errorMessage(_error: unknown): LocalizedMessage {
    return msg({ id: 'forwardedOnboarding.loadFailure', message: 'Unable to load your forwarded imports right now.' });
}

export function useForwardedOnboardingState({
    areProjectsLoading: providedProjectsLoading = false,
    nonInboxProjectCount: providedNonInboxProjectCount,
    projectsError: providedProjectsError = null,
    skip = false,
    pageSize = 10,
}: UseForwardedOnboardingStateOptions = {}): UseForwardedOnboardingStateResult {
    const { session, isLoading: isSessionLoading } = useSession();
    const shouldLoadProjects = typeof providedNonInboxProjectCount !== 'number';
    const {
        projects,
        isLoading: areProjectsLoading,
        error: projectsError,
    } = useUserProjects({ enabled: shouldLoadProjects });
    const [pageData, setPageData] = useState<PaperInboxPageData>(EMPTY_PAPER_INBOX_PAGE_DATA);
    const [isInboxLoading, setIsInboxLoading] = useState(!skip);
    const [inboxError, setInboxError] = useState<LocalizedMessage | null>(null);
    const userId = canAccessMainApp(session) ? session?.user?.id ?? null : null;

    const load = useCallback(async () => {
        if (skip) {
            setPageData(EMPTY_PAPER_INBOX_PAGE_DATA);
            setInboxError(null);
            setIsInboxLoading(false);
            return;
        }

        if (isSessionLoading) {
            setIsInboxLoading(true);
            return;
        }

        if (!userId) {
            setPageData(EMPTY_PAPER_INBOX_PAGE_DATA);
            setInboxError(null);
            setIsInboxLoading(false);
            return;
        }

        setIsInboxLoading(true);
        try {
            const nextPageData = await fetchPaperInboxPageDataAfterReadiness({
                page: 0,
                pageSize,
                userId,
            });
            setPageData(nextPageData);
            setInboxError(null);
        } catch (nextError) {
            setPageData(EMPTY_PAPER_INBOX_PAGE_DATA);
            setInboxError(errorMessage(nextError));
        } finally {
            setIsInboxLoading(false);
        }
    }, [isSessionLoading, pageSize, skip, userId]);

    useEffect(() => {
        void load();
    }, [load]);

    const snapshot = useMemo(() => (
        buildForwardedOnboardingSnapshot({
            imports: pageData.imports,
            candidates: pageData.candidates,
        })
    ), [pageData.candidates, pageData.imports]);

    const nonInboxProjectCount = providedNonInboxProjectCount ?? projects.length;
    const resolvedProjectsLoading = shouldLoadProjects ? areProjectsLoading : providedProjectsLoading;
    const resolvedProjectsError = shouldLoadProjects ? projectsError : providedProjectsError;
    const isEligible = nonInboxProjectCount === 0 && snapshot.importCount > 0;

    return {
        ...snapshot,
        nonInboxProjectCount,
        fallbackMessage: fallbackForwardedImportMessage(pageData.imports),
        isEligible,
        isLoading: !skip && (isSessionLoading || resolvedProjectsLoading || isInboxLoading),
        error: inboxError || (nonInboxProjectCount === 0 ? resolvedProjectsError : null),
        refresh: load,
    };
}
